import { Injectable } from '@angular/core'
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs'
import { filter, tap, map, throttleTime, catchError } from 'rxjs/operators'
import { Router, NavigationStart } from '@angular/router'
import { AuthService } from '../../services/auth.service'
import { appMenu } from './../../config/menu/app-menu'
import { IUserGroup } from './../interfaces/user-group.interface'
import { Vendor } from '../../models/common/vendor'
import { UserDataService } from '../../services/shared/user-data.service'

export interface IMenuConfig {
  config: {
    header: {
      self: any // { }
      items: any[]
    }
    aside: {
      self: any // { }
      items: any[]
    }
  }
}

export const emptyMenuConfig: IMenuConfig = {
  config: {
    header: {
      self: {},
      items: [],
    },
    aside: {
      self: {},
      items: [],
    },
  },
}

@Injectable()
export class MenuConfigService {
  public configModel: IMenuConfig = emptyMenuConfig
  public onMenuUpdated$: BehaviorSubject<any> = new BehaviorSubject(
    this.configModel
  )
  menuHasChanged: any = false

  public blankLayoutPages = [
    '/login',
    '/register',
    '/account-verification',
    '/password-reset',
    '/404',
    '/error',
    '/dev/icons-preview',
    '/auth',
    '/change-password',
  ]

  constructor(
    private router: Router,
    private authService: AuthService,
    private userDataService: UserDataService
  ) // private privateLabelService: PrivateLabelService,
  // private tokenService: TokenService
  {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        throttleTime(1000)
      )
      .subscribe((event: NavigationStart) => {
        // if (!event.url.includes('login') && !event.url.includes('register')) {
        // 	this.buildMenu().subscribe();
        // }
        // this.resetModel();
        // console.log('menu updated');
        // if (this.menuHasChanged) {
        // 	console.log('menu changed');
        // 	this.resetModel();
        // }
      })
    // this.initializeMenu()
    if (
      this.blankLayoutPages.every((page) => !this.router.url.includes(page))
    ) {
      this.buildMenu().subscribe()
    }
  }

  // setModel(menuModel: MenuConfig) {
  // 	this.configModel = Object.assign(this.configModel, menuModel);
  // 	this.onMenuUpdated$.next(this.applyPermissions(this.configModel));
  // 	// this.menuHasChanged = true;
  // }

  // resetModel() {
  // 	this.configModel = new MenuConfig(this.tokenService);
  // 	this.onMenuUpdated$.next(this.applyPermissions(this.configModel));
  // 	// this.menuHasChanged = false;
  // }

  public buildMenu(fromCache = true): Observable<IMenuConfig> {
    return forkJoin(
      this.userDataService.getData().pipe(
        map((data) =>
          data.private_label
            ? data.private_label
            : { is_private_label: false, manufacturer: null }
        ),
        catchError(() => of({ is_private_label: false, manufacturer: null }))
      ),
      this.authService.getUserGroup(fromCache)
    ).pipe(
      map(([privateLabelData, userGroupData]) => {
        let menu: IMenuConfig = emptyMenuConfig
        menu.config.aside.items = this.getModuleInitialMenu(
          userGroupData.module
        )
        menu = this.getEnabledMenuItems(menu, userGroupData.user_group)
        if (
          userGroupData.module == 'showroom' &&
          privateLabelData.is_private_label
        ) {
          menu = this.getPrivateLabelMenuItems(menu)
          menu = this.replaceManufacturerSettingsPage(
            menu,
            privateLabelData.manufacturer
          )
        }
        return menu
      }),
      tap((menu) => this.onMenuUpdated$.next(menu))
    )
    // return this.authService.getUserGroup(fromCache)
    // 	.pipe(
    // 		map(data => {
    // 			let menu: IMenuConfig = emptyMenuConfig;
    // 			menu.config.aside.items = this.getModuleInitialMenu(data.module);
    // 			menu = this.getEnabledMenuItems(menu, data.user_group);
    // 			return menu;
    // 		}),
    // 		tap(menu => {
    // 			this.onMenuUpdated$.next(menu);
    // 		}),
    // 	);
  }

  private getModuleInitialMenu(
    moduleName: 'showroom' | 'vendor' | 'superadmin'
  ): any[] {
    if (moduleName == 'showroom') {
      return appMenu.showroomMenu
    }
    if (moduleName == 'vendor') {
      return appMenu.vendorMenu
    }
    if (moduleName == 'superadmin') {
      return appMenu.superadminMenu
    }
    console.error('Invalid app module: [' + moduleName + ']')
    return []
  }

  private getPrivateLabelMenuItems(menu: IMenuConfig): IMenuConfig {
    menu.config.aside.items = menu.config.aside.items.map((item) => {
      if (!item.submenu) {
        return item
      }
      return {
        ...item,
        submenu: item.submenu.filter(
          (submenuItem) => !submenuItem.privateLabelOnly
        ),
      }
    })
    menu.config.aside.items = menu.config.aside.items.filter((item) => {
      // If submenu items were previously removed then we remove the root menu item
      if (item.submenu && !item.submenu.length) {
        return false
      }
      if (item.privateLabelOnly == undefined) {
        return true
      }
      return item.privateLabelOnly == true
    })
    return menu
  }

  private replaceManufacturerSettingsPage(
    menu: IMenuConfig,
    vendor: Vendor
  ): IMenuConfig {
    menu.config.aside.items = menu.config.aside.items.map((item) => {
      if (item.title != 'Settings') {
        return item
      }
      return {
        ...item,
        submenu: item.submenu.map((submenuItem) => {
          if (submenuItem.title != 'Manufacturers') {
            return submenuItem
          }
          return {
            ...submenuItem,
            title: 'Manufacturer',
            page: `/showroom/settings/vendors/${vendor.pivot.id}/edit`,
          }
        }),
      }
    })
    return menu
  }

  private getEnabledMenuItems(
    menu: IMenuConfig,
    userGroup: IUserGroup
  ): IMenuConfig {
    // First remove the disabled submenu items
    menu = this.removeDisabledSubmenuItems(menu, userGroup)
    // Then remove disabled top level items or items having empty submenu
    return this.removeDisabledMenuItems(menu, userGroup)
  }

  private removeDisabledMenuItems(
    menu: IMenuConfig,
    userGroup: IUserGroup
  ): IMenuConfig {
    menu.config.aside.items = menu.config.aside.items.filter((item) => {
      // If submenu items were previously removed then we remove the root menu item
      if (item.submenu && !item.submenu.length) {
        return false
      }
      // If there is no actionId defined in menu item it is considered enabled
      if (item.actionId == undefined) {
        return true
      }
      return this.isFeatureActionEnabled(item.actionId, userGroup)
    })
    return menu
  }

  private removeDisabledSubmenuItems(
    menu: IMenuConfig,
    userGroup: IUserGroup
  ): IMenuConfig {
    menu.config.aside.items = menu.config.aside.items.map((item) => {
      if (!item.submenu) {
        return item
      }
      return {
        ...item,
        submenu: item.submenu.filter((submenuItem) => {
          if (submenuItem.actionId == undefined) {
            return submenuItem
          }
          return this.isFeatureActionEnabled(submenuItem.actionId, userGroup)
        }),
      }
    })
    return menu
  }

  private isFeatureActionEnabled(
    actionId: number,
    userGroup: IUserGroup
  ): boolean {
    for (let i = 0; i < userGroup.features.length; i++) {
      const feature = userGroup.features[i]
      for (let j = 0; j < feature.actions.length; j++) {
        const action = feature.actions[j]
        if (action.id == actionId && !action.enabled) {
          return false
        }
      }
    }
    return true
  }
}
